import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '@/hooks/useApi';
import { apiFetch } from '@/lib/api';
import {
  Button,
  Badge,
  StatCard,
  PageHeader,
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
  EmptyState,
  Spinner,
} from '@theonefamily/ui';
import { Truck, Plus, Activity, Wrench, Gauge, X, Loader2 } from 'lucide-react';

interface Vehicle {
  id: string;
  name: string;
  vehicle_number: string;
  make: string;
  model: string;
  year: number;
  license_plate: string;
  status: 'active' | 'maintenance' | 'retired' | 'sold';
  assigned_driver_name: string | null;
  mileage: number;
  vehicle_type: string;
  fuel_type: string;
}

interface VehicleListData {
  vehicles: Vehicle[];
  total: number;
  active: number;
  inMaintenance: number;
  avgMileage: number;
}

const STATUS_VARIANT: Record<string, 'success' | 'destructive' | 'secondary' | 'warning'> = {
  active: 'success',
  maintenance: 'destructive',
  retired: 'secondary',
  sold: 'warning',
};

const VEHICLE_TYPES = ['car', 'van', 'truck', 'suv', 'trailer', 'other'] as const;
const FUEL_TYPES = ['gasoline', 'diesel', 'electric', 'hybrid'] as const;
const OWNERSHIP_TYPES = ['owned', 'leased', 'rented'] as const;

export default function VehicleListPage() {
  const navigate = useNavigate();
  const { data, isLoading, refetch } = useApi<VehicleListData>('/api/vehicles');
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const vehicles = data?.vehicles ?? [];
  const total = data?.total ?? 0;
  const active = data?.active ?? 0;
  const inMaintenance = data?.inMaintenance ?? 0;
  const avgMileage = data?.avgMileage ?? 0;

  const handleSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSaving(true);
    setFormError('');
    const fd = new FormData(e.currentTarget);
    try {
      await apiFetch('/api/vehicles', {
        method: 'POST',
        body: JSON.stringify({
          name: fd.get('name'),
          make: fd.get('make') || undefined,
          model: fd.get('model') || undefined,
          year: fd.get('year') ? Number(fd.get('year')) : undefined,
          vin: fd.get('vin') || undefined,
          license_plate: fd.get('license_plate') || undefined,
          color: fd.get('color') || undefined,
          vehicle_type: fd.get('vehicle_type') || 'car',
          fuel_type: fd.get('fuel_type') || 'gasoline',
          ownership_type: fd.get('ownership_type') || 'owned',
          mileage: fd.get('mileage') ? Number(fd.get('mileage')) : 0,
          department: fd.get('department') || undefined,
          purchase_date: fd.get('purchase_date') || undefined,
          purchase_price: fd.get('purchase_price') ? Number(fd.get('purchase_price')) : undefined,
          insurance_policy: fd.get('insurance_policy') || undefined,
          insurance_expiry: fd.get('insurance_expiry') || undefined,
          registration_expiry: fd.get('registration_expiry') || undefined,
          notes: fd.get('notes') || undefined,
        }),
      });
      setShowForm(false);
      refetch();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to create vehicle');
    } finally {
      setSaving(false);
    }
  }, [refetch]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Vehicles"
        description="Manage your fleet vehicles"
        actions={
          <Button onClick={() => setShowForm(true)}>
            <Plus size={14} />
            Add Vehicle
          </Button>
        }
      />

      {/* Metric Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Truck} label="Total" value={total} />
        <StatCard icon={Activity} label="Active" value={active} accent="success" />
        <StatCard icon={Wrench} label="In Maintenance" value={inMaintenance} accent="destructive" />
        <StatCard icon={Gauge} label="Avg Mileage" value={`${avgMileage.toLocaleString()} mi`} />
      </div>

      {/* Table */}
      <div
        className="rounded-xl overflow-hidden"
        style={{ background: 'var(--surface-1)', border: '1px solid var(--border-subtle)' }}
      >
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Spinner size={24} />
          </div>
        ) : vehicles.length === 0 ? (
          <EmptyState
            icon={Truck}
            title="No vehicles found"
            description="Add your first vehicle to get started."
            action={{ label: 'Add Vehicle', onClick: () => setShowForm(true) }}
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Vehicle</TableHead>
                <TableHead>Make / Model</TableHead>
                <TableHead>Year</TableHead>
                <TableHead>Plate</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Assigned To</TableHead>
                <TableHead className="text-right">Mileage</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {vehicles.map((v) => (
                <TableRow
                  key={v.id}
                  onClick={() => navigate(`/vehicles/${v.id}`)}
                  className="cursor-pointer"
                >
                  <TableCell className="font-medium">{v.name}</TableCell>
                  <TableCell>{v.make} {v.model}</TableCell>
                  <TableCell>{v.year}</TableCell>
                  <TableCell className="font-mono text-xs">{v.license_plate}</TableCell>
                  <TableCell className="capitalize">{v.vehicle_type}</TableCell>
                  <TableCell>
                    <Badge variant={STATUS_VARIANT[v.status] ?? 'secondary'}>
                      {v.status.replace('_', ' ')}
                    </Badge>
                  </TableCell>
                  <TableCell style={{ color: 'var(--muted-foreground)' }}>{v.assigned_driver_name ?? '--'}</TableCell>
                  <TableCell className="text-right font-mono text-sm">{v.mileage.toLocaleString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Create Vehicle Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.6)' }}>
          <div className="w-full max-w-2xl rounded-xl p-6 max-h-[90vh] overflow-y-auto"
            style={{ background: 'var(--surface-1)', border: '1px solid var(--border-subtle)' }}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold" style={{ color: 'var(--foreground)' }}>Add Vehicle</h2>
              <button onClick={() => setShowForm(false)} className="p-1 rounded hover:bg-white/10">
                <X className="w-4 h-4" style={{ color: 'var(--muted-foreground)' }} />
              </button>
            </div>
            {formError && (
              <div className="mb-4 p-3 rounded-lg text-sm" style={{ background: 'rgba(239,68,68,0.1)', color: '#EF4444' }}>{formError}</div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <FormField label="Vehicle Name" required>
                <input type="text" name="name" required className="form-input" placeholder="e.g. Service Van #1" />
              </FormField>
              <div className="grid grid-cols-3 gap-4">
                <FormField label="Make">
                  <input type="text" name="make" className="form-input" placeholder="e.g. Ford" />
                </FormField>
                <FormField label="Model">
                  <input type="text" name="model" className="form-input" placeholder="e.g. Transit" />
                </FormField>
                <FormField label="Year">
                  <input type="number" name="year" className="form-input" placeholder="2024" min="1900" max="2100" />
                </FormField>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FormField label="VIN">
                  <input type="text" name="vin" className="form-input" placeholder="Vehicle Identification Number" maxLength={17} />
                </FormField>
                <FormField label="License Plate">
                  <input type="text" name="license_plate" className="form-input" placeholder="ABC-1234" />
                </FormField>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <FormField label="Vehicle Type">
                  <select name="vehicle_type" className="form-select" defaultValue="car">
                    {VEHICLE_TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                  </select>
                </FormField>
                <FormField label="Fuel Type">
                  <select name="fuel_type" className="form-select" defaultValue="gasoline">
                    {FUEL_TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                  </select>
                </FormField>
                <FormField label="Ownership">
                  <select name="ownership_type" className="form-select" defaultValue="owned">
                    {OWNERSHIP_TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                  </select>
                </FormField>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FormField label="Color">
                  <input type="text" name="color" className="form-input" placeholder="e.g. White" />
                </FormField>
                <FormField label="Current Mileage">
                  <input type="number" name="mileage" className="form-input" placeholder="0" min="0" />
                </FormField>
              </div>
              <FormField label="Department">
                <input type="text" name="department" className="form-input" placeholder="e.g. Field Services" />
              </FormField>
              <div className="grid grid-cols-2 gap-4">
                <FormField label="Purchase Date">
                  <input type="date" name="purchase_date" className="form-input" />
                </FormField>
                <FormField label="Purchase Price ($)">
                  <input type="number" name="purchase_price" step="0.01" min="0" className="form-input" />
                </FormField>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FormField label="Insurance Policy #">
                  <input type="text" name="insurance_policy" className="form-input" />
                </FormField>
                <FormField label="Insurance Expiry">
                  <input type="date" name="insurance_expiry" className="form-input" />
                </FormField>
              </div>
              <FormField label="Registration Expiry">
                <input type="date" name="registration_expiry" className="form-input" />
              </FormField>
              <FormField label="Notes">
                <textarea name="notes" rows={2} className="form-input" placeholder="Additional notes..." />
              </FormField>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowForm(false)}
                  className="px-4 py-2 text-sm rounded-lg" style={{ color: 'var(--muted-foreground)' }}>Cancel</button>
                <button type="submit" disabled={saving}
                  className="px-4 py-2 text-sm font-medium text-foreground rounded-lg flex items-center gap-2"
                  style={{ background: 'var(--accent)', opacity: saving ? 0.7 : 1 }}>
                  {saving && <Loader2 className="w-3 h-3 animate-spin" />}
                  Add Vehicle
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function FormField({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-sm font-medium mb-1" style={{ color: 'var(--foreground)' }}>
        {label}{required && <span style={{ color: '#EF4444' }}> *</span>}
      </label>
      {children}
    </div>
  );
}
